import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocalePrefs } from './prefs.ts';
import type { MessageKey } from './messages.ts';
import {
  formatDateLong,
  formatDateShort,
  formatDateWeekday,
  formatDateRange,
  formatDateTime,
  formatTime,
  formatWeekday, formatDayNumber, formatMonthName,
  formatDelay,
  formatDuration,
  formatDistance,
  formatNumber,
  formatPercent,
  formatRelative,
  formatToman,
  formatTomanCompact,
  withDigits,
  type DateFmtCtx,
} from './format/index.ts';

type Values = Record<string, string | number | boolean | Date | null | undefined | ((chunks: React.ReactNode[]) => React.ReactNode)>;

/** Typed message hook: t('auth.otp.title'), t('trips.dayN', { n: 2 }). */
export function useT() {
  const intl = useIntl();
  return (id: MessageKey, values?: Values) => intl.formatMessage({ id }, values as Record<string, never>);
}

/** Locale-aware formatters bound to the user's preferences (locale, digits, calendar). */
export function useFormat() {
  const prefs = useLocalePrefs();
  return useMemo(() => {
    const ctx: DateFmtCtx = { locale: prefs.locale, digits: prefs.digits, calendar: prefs.calendar, timeZone: prefs.timeZone };
    const { locale, digits } = prefs;
    return {
      prefs,
      dateLong: (d: Date | string | number) => formatDateLong(d, ctx),
      dateShort: (d: Date | string | number) => formatDateShort(d, ctx),
      dateWeekday: (d: Date | string | number) => formatDateWeekday(d, ctx),
      weekday: (d: Date | string | number) => formatWeekday(d, ctx),
      dayNumber: (d: Date | string | number) => formatDayNumber(d, ctx),
      monthName: (d: Date | string | number) => formatMonthName(d, ctx),
      dateRange: (a: Date | string | number, b: Date | string | number) => formatDateRange(a, b, ctx),
      time: (d: Date | string | number) => formatTime(d, ctx),
      dateTime: (d: Date | string | number) => formatDateTime(d, ctx),
      relative: (d: Date | string | number, now?: number) => formatRelative(d, locale, digits, now),
      number: (n: number, opts?: Intl.NumberFormatOptions) => formatNumber(n, locale, digits, opts),
      percent: (r: number, fd?: number) => formatPercent(r, locale, digits, fd),
      distance: (m: number) => formatDistance(m, locale, digits),
      duration: (min: number, short?: boolean) => formatDuration(min, locale, digits, { short }),
      delay: (min: number) => formatDelay(min, locale, digits),
      toman: (t: number, unit?: boolean) => formatToman(t, locale, digits, { unit }),
      tomanCompact: (t: number, unit?: boolean) => formatTomanCompact(t, locale, digits, { unit }),
      digits: (s: string | number) => withDigits(String(s), digits),
    };
  }, [prefs]);
}
